import { useMutation, useQuery } from '@apollo/client/react';
import {
  ChevronDownIcon,
  EllipsisVerticalIcon,
  PenIcon,
  PlusIcon,
  Trash2Icon
} from 'lucide-react';
import { type PropsWithChildren, useState } from 'react';
import { toast } from 'sonner';

import {
  DELETE_DEAL_DRIVER,
  DELETE_DEAL_SCENARIO,
  GET_ALL_DRIVERS,
  GET_ALL_SCENARIOS,
  UPSERT_DEAL_DRIVER,
  UPSERT_DEAL_SCENARIO
} from './graphql';
import type {
  DealDriver,
  DealDriverScenario,
  GetAllDriversResponse,
  GetAllScenariosResponse
} from './types';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const DRIVER_TYPES = [
  { label: 'Revenue', value: 'revenue' },
  { label: 'Capex', value: 'capex' },
  { label: 'Cost Structure', value: 'cost_structure' },
  { label: 'Working Capital', value: 'working_capital' }
] as const;

const PROBABILITY_LEVELS: Record<string, string> = {
  high: 'High Prob',
  low: 'Low Prob',
  low_medium: 'Low-Med Prob',
  medium: 'Medium Prob',
  medium_high: 'Med-High Prob'
};

const IMPACT_TYPES: Record<string, string> = {
  capex: 'Capex',
  ebitda_margin: 'EBITDA Margin',
  gross_margin: 'Gross Margin Impact',
  labor_cost: 'Labor Cost',
  revenue: 'Revenue Impact',
  unplanned_capex: 'Unplanned Capex',
  wc: 'WC'
};

const CONFIRM_DELETE_DESCRIPTION =
  'This action cannot be undone. This will permanently delete this item.';

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

// ── Drivers Card ──────────────────────────────────────────────────

const EditDriverDialog = ({
  dealId,
  driver,
  onOpenChange,
  open
}: {
  dealId: string;
  driver: DealDriver;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) => {
  const [upsertDriver, { loading: isUpdating }] = useMutation(
    UPSERT_DEAL_DRIVER,
    { refetchQueries: [GET_ALL_DRIVERS] }
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const title =
        (formData.get('edit-driver-title') as string)?.trim() || null;
      const subtitle =
        (formData.get('edit-driver-subtitle') as string)?.trim() || null;

      const { errors } = await upsertDriver({
        variables: {
          changesetArray: [{ id: driver.id, subtitle, title }],
          dealOpportunityId: dealId
        }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      onOpenChange(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Edit Driver
            <Badge variant="yellow">Unsaved</Badge>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id={`edit-driver-${driver.id}-form`}
            noValidate
            onSubmit={event => void handleSubmit(event)}
          >
            <FieldGroup>
              <Field>
                <FieldLabel htmlFor="edit-driver-title">Title</FieldLabel>
                <Input
                  defaultValue={driver.title ?? ''}
                  id="edit-driver-title"
                  name="edit-driver-title"
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="edit-driver-subtitle">Subtitle</FieldLabel>
                <Input
                  defaultValue={driver.subtitle ?? ''}
                  id="edit-driver-subtitle"
                  name="edit-driver-subtitle"
                />
              </Field>
            </FieldGroup>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            disabled={isUpdating}
            form={`edit-driver-${driver.id}-form`}
            type="submit"
          >
            {isUpdating && <Spinner />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DriversCard = ({
  dealId,
  drivers,
  driverType
}: {
  dealId: string;
  drivers: DealDriver[];
  driverType: string;
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteDriverId, setDeleteDriverId] = useState<null | string>(null);
  const [editDriverId, setEditDriverId] = useState<null | string>(null);

  const [upsertDriver, { loading: isUpdating }] = useMutation(
    UPSERT_DEAL_DRIVER,
    { refetchQueries: [GET_ALL_DRIVERS] }
  );

  const [deleteDriver, { loading: isDeleting }] = useMutation(
    DELETE_DEAL_DRIVER,
    { refetchQueries: [GET_ALL_DRIVERS] }
  );

  const handleDelete = async (id: string) => {
    try {
      const { errors } = await deleteDriver({ variables: { id } });
      if (errors?.length) {
        throw new Error(errors[0].message);
      }
      setConfirmDeleteOpen(false);
      setDeleteDriverId(null);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleAddDriver = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const title =
        (formData.get('new-driver-title') as string)?.trim() || null;
      const subtitle =
        (formData.get('new-driver-subtitle') as string)?.trim() || null;

      const { errors } = await upsertDriver({
        variables: {
          dealOpportunityId: dealId,
          newRows: [{ driverType, subtitle, title }]
        }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      setIsAddOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h4 className="text-base font-semibold">Drivers</h4>
        <Dialog
          onOpenChange={setIsAddOpen}
          open={isAddOpen}
        >
          {drivers.length !== 0 && (
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Add Driver
              </Button>
            </DialogTrigger>
          )}
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                Add Driver
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id={`add-driver-${driverType}-form`}
                noValidate
                onSubmit={event => void handleAddDriver(event)}
              >
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="new-driver-title">Title</FieldLabel>
                    <Input
                      id="new-driver-title"
                      name="new-driver-title"
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="new-driver-subtitle">
                      Subtitle
                    </FieldLabel>
                    <Input
                      id="new-driver-subtitle"
                      name="new-driver-subtitle"
                    />
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isUpdating}
                form={`add-driver-${driverType}-form`}
                type="submit"
              >
                {isUpdating && <Spinner />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Subtitle</TableHead>
            <TableHead className="bg-card sticky right-0 z-10 w-0" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {drivers.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                className="p-0"
                colSpan={3}
              >
                <Empty className="px-0! pt-5! pb-0!">
                  <EmptyContent>
                    <Button
                      onClick={() => {
                        setIsAddOpen(true);
                      }}
                    >
                      <PlusIcon />
                      Add Driver
                    </Button>
                  </EmptyContent>
                </Empty>
              </TableCell>
            </TableRow>
          ) : (
            drivers.map(driver => (
              <TableRow key={driver.id}>
                <TableCell>{driver.title ?? '-'}</TableCell>
                <TableCell>{driver.subtitle ?? '-'}</TableCell>
                <TableCell className="bg-card sticky right-0 z-10 w-0">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        className="bg-card shadow-xs"
                        size="icon-sm"
                        variant="ghost"
                      >
                        <EllipsisVerticalIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onSelect={() => {
                          setEditDriverId(driver.id);
                        }}
                      >
                        <PenIcon />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        disabled={isDeleting}
                        onSelect={() => {
                          setDeleteDriverId(driver.id);
                          setConfirmDeleteOpen(true);
                        }}
                        variant="destructive"
                      >
                        <Trash2Icon />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <EditDriverDialog
                    dealId={dealId}
                    driver={driver}
                    onOpenChange={open => {
                      if (!open) {
                        setEditDriverId(null);
                      }
                    }}
                    open={editDriverId === driver.id}
                  />
                  <AlertDialog
                    onOpenChange={setConfirmDeleteOpen}
                    open={confirmDeleteOpen && deleteDriverId === driver.id}
                  >
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          Are you absolutely sure?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {CONFIRM_DELETE_DESCRIPTION}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          disabled={isDeleting}
                          onClick={() => {
                            if (deleteDriverId) {
                              void handleDelete(deleteDriverId);
                            }
                          }}
                          variant="destructive"
                        >
                          {isDeleting && <Spinner />}
                          Continue
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Card>
  );
};

// ── Scenarios Card ────────────────────────────────────────────────

const ScenarioFormFields = ({
  defaultValues,
  idPrefix,
  impactType,
  probabilityLevel,
  setImpactType,
  setProbabilityLevel
}: {
  defaultValues?: {
    impactRevenueMax?: null | number;
    impactRevenueMin?: null | number;
    impactText?: null | string;
    title?: null | string;
  };
  idPrefix: string;
  impactType: string;
  probabilityLevel: string;
  setImpactType: (v: string) => void;
  setProbabilityLevel: (v: string) => void;
}) => (
  <FieldGroup>
    <Field>
      <FieldLabel htmlFor={`${idPrefix}-title`}>Title</FieldLabel>
      <Input
        defaultValue={defaultValues?.title ?? ''}
        id={`${idPrefix}-title`}
        name={`${idPrefix}-title`}
      />
    </Field>
    <Field>
      <FieldLabel>Probability Level</FieldLabel>
      <Select
        onValueChange={setProbabilityLevel}
        value={probabilityLevel}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(PROBABILITY_LEVELS).map(([k, v]) => (
            <SelectItem
              key={k}
              value={k}
            >
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
    <Field>
      <FieldLabel>Impact Type</FieldLabel>
      <Select
        onValueChange={setImpactType}
        value={impactType}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select..." />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(IMPACT_TYPES).map(([k, v]) => (
            <SelectItem
              key={k}
              value={k}
            >
              {v}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
    <Field>
      <FieldLabel htmlFor={`${idPrefix}-impact-text`}>Impact Text</FieldLabel>
      <Input
        defaultValue={defaultValues?.impactText ?? ''}
        id={`${idPrefix}-impact-text`}
        name={`${idPrefix}-impact-text`}
      />
    </Field>
    <div className="grid grid-cols-2 gap-4">
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-revenue-min`}>
          Impact Revenue Min
        </FieldLabel>
        <Input
          defaultValue={defaultValues?.impactRevenueMin ?? ''}
          id={`${idPrefix}-revenue-min`}
          name={`${idPrefix}-revenue-min`}
          type="number"
        />
      </Field>
      <Field>
        <FieldLabel htmlFor={`${idPrefix}-revenue-max`}>
          Impact Revenue Max
        </FieldLabel>
        <Input
          defaultValue={defaultValues?.impactRevenueMax ?? ''}
          id={`${idPrefix}-revenue-max`}
          name={`${idPrefix}-revenue-max`}
          type="number"
        />
      </Field>
    </div>
  </FieldGroup>
);

const EditScenarioDialog = ({
  dealId,
  onOpenChange,
  open,
  scenario
}: {
  dealId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  scenario: DealDriverScenario;
}) => {
  const [probabilityLevel, setProbabilityLevel] = useState(
    scenario.probabilityLevel ?? ''
  );
  const [impactType, setImpactType] = useState(scenario.impactType ?? '');

  const [upsertScenario, { loading: isUpdating }] = useMutation(
    UPSERT_DEAL_SCENARIO,
    { refetchQueries: [GET_ALL_SCENARIOS] }
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const title =
        (formData.get('edit-scenario-title') as string)?.trim() || null;
      const impactText =
        (formData.get('edit-scenario-impact-text') as string)?.trim() || null;
      const impactRevenueMin =
        Number(formData.get('edit-scenario-revenue-min')) || null;
      const impactRevenueMax =
        Number(formData.get('edit-scenario-revenue-max')) || null;

      const { errors } = await upsertScenario({
        variables: {
          changesetArray: [
            {
              id: scenario.id,
              impactRevenueMax,
              impactRevenueMin,
              impactText,
              impactType: impactType || null,
              probabilityLevel: probabilityLevel || null,
              title
            }
          ],
          dealOpportunityId: dealId
        }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      onOpenChange(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Edit Scenario & Impact
            <Badge variant="yellow">Unsaved</Badge>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form
            id={`edit-scenario-${scenario.id}-form`}
            noValidate
            onSubmit={event => void handleSubmit(event)}
          >
            <ScenarioFormFields
              defaultValues={scenario}
              idPrefix="edit-scenario"
              impactType={impactType}
              probabilityLevel={probabilityLevel}
              setImpactType={setImpactType}
              setProbabilityLevel={setProbabilityLevel}
            />
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            disabled={isUpdating}
            form={`edit-scenario-${scenario.id}-form`}
            type="submit"
          >
            {isUpdating && <Spinner />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ScenariosCard = ({
  dealId,
  driverType,
  scenarios
}: {
  dealId: string;
  driverType: string;
  scenarios: DealDriverScenario[];
}) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteScenarioId, setDeleteScenarioId] = useState<null | string>(null);
  const [editScenarioId, setEditScenarioId] = useState<null | string>(null);
  const [probabilityLevel, setProbabilityLevel] = useState('');
  const [impactType, setImpactType] = useState('');

  const [upsertScenario, { loading: isUpdating }] = useMutation(
    UPSERT_DEAL_SCENARIO,
    { refetchQueries: [GET_ALL_SCENARIOS] }
  );

  const [deleteScenario, { loading: isDeleting }] = useMutation(
    DELETE_DEAL_SCENARIO,
    { refetchQueries: [GET_ALL_SCENARIOS] }
  );

  const handleDelete = async (id: string) => {
    try {
      const { errors } = await deleteScenario({ variables: { id } });
      if (errors?.length) {
        throw new Error(errors[0].message);
      }
      setConfirmDeleteOpen(false);
      setDeleteScenarioId(null);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  const handleAddScenario = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const title =
        (formData.get('new-scenario-title') as string)?.trim() || null;
      const impactText =
        (formData.get('new-scenario-impact-text') as string)?.trim() || null;
      const impactRevenueMin =
        Number(formData.get('new-scenario-revenue-min')) || null;
      const impactRevenueMax =
        Number(formData.get('new-scenario-revenue-max')) || null;

      const { errors } = await upsertScenario({
        variables: {
          dealOpportunityId: dealId,
          newRows: [
            {
              driverType,
              impactRevenueMax,
              impactRevenueMin,
              impactText,
              impactType: impactType || null,
              probabilityLevel: probabilityLevel || null,
              title
            }
          ]
        }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      setIsAddOpen(false);
      setProbabilityLevel('');
      setImpactType('');
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <Card>
      <div className="mb-2 flex items-center justify-between gap-3">
        <h4 className="text-base font-semibold">Scenarios and Impacts</h4>
        <Dialog
          onOpenChange={setIsAddOpen}
          open={isAddOpen}
        >
          {scenarios.length !== 0 && (
            <DialogTrigger asChild>
              <Button>
                <PlusIcon />
                Add Scenario
              </Button>
            </DialogTrigger>
          )}
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                Add Scenario & Impact
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id={`add-scenario-${driverType}-form`}
                noValidate
                onSubmit={event => void handleAddScenario(event)}
              >
                <ScenarioFormFields
                  idPrefix="new-scenario"
                  impactType={impactType}
                  probabilityLevel={probabilityLevel}
                  setImpactType={setImpactType}
                  setProbabilityLevel={setProbabilityLevel}
                />
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isUpdating}
                form={`add-scenario-${driverType}-form`}
                type="submit"
              >
                {isUpdating && <Spinner />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Subtitle</TableHead>
              <TableHead>Probability Level</TableHead>
              <TableHead>Impact Type</TableHead>
              <TableHead>Impact Text</TableHead>
              <TableHead className="text-right">Impact Revenue Min</TableHead>
              <TableHead className="text-right">Impact Revenue Max</TableHead>
              <TableHead className="bg-card sticky right-0 z-10 w-0" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {scenarios.length === 0 ? (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  className="p-0"
                  colSpan={8}
                >
                  <Empty className="px-0! pt-5! pb-0!">
                    <EmptyContent>
                      <Button
                        onClick={() => {
                          setIsAddOpen(true);
                        }}
                      >
                        <PlusIcon />
                        Add Scenario
                      </Button>
                    </EmptyContent>
                  </Empty>
                </TableCell>
              </TableRow>
            ) : (
              scenarios.map(scenario => (
                <TableRow key={scenario.id}>
                  <TableCell>{scenario.title ?? '-'}</TableCell>
                  <TableCell className="max-w-48 truncate">
                    {scenario.description ?? '-'}
                  </TableCell>
                  <TableCell>
                    {scenario.probabilityLevel
                      ? (PROBABILITY_LEVELS[scenario.probabilityLevel] ??
                        scenario.probabilityLevel)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {scenario.impactType
                      ? (IMPACT_TYPES[scenario.impactType] ??
                        scenario.impactType)
                      : '-'}
                  </TableCell>
                  <TableCell className="max-w-48 truncate">
                    {scenario.impactText ?? '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {scenario.impactRevenueMin ?? '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    {scenario.impactRevenueMax ?? '-'}
                  </TableCell>
                  <TableCell className="bg-card sticky right-0 z-10 w-0">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          className="bg-card shadow-xs"
                          size="icon-sm"
                          variant="ghost"
                        >
                          <EllipsisVerticalIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onSelect={() => {
                            setEditScenarioId(scenario.id);
                          }}
                        >
                          <PenIcon />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={isDeleting}
                          onSelect={() => {
                            setDeleteScenarioId(scenario.id);
                            setConfirmDeleteOpen(true);
                          }}
                          variant="destructive"
                        >
                          <Trash2Icon />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <EditScenarioDialog
                      dealId={dealId}
                      onOpenChange={open => {
                        if (!open) {
                          setEditScenarioId(null);
                        }
                      }}
                      open={editScenarioId === scenario.id}
                      scenario={scenario}
                    />
                    <AlertDialog
                      onOpenChange={setConfirmDeleteOpen}
                      open={
                        confirmDeleteOpen && deleteScenarioId === scenario.id
                      }
                    >
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Are you absolutely sure?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            {CONFIRM_DELETE_DESCRIPTION}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            disabled={isDeleting}
                            onClick={() => {
                              if (deleteScenarioId) {
                                void handleDelete(deleteScenarioId);
                              }
                            }}
                            variant="destructive"
                          >
                            {isDeleting && <Spinner />}
                            Continue
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
};

// ── Driver Type Section (Collapsible) ─────────────────────────────

const DriverTypeSection = ({
  dealId,
  drivers,
  label,
  scenarios,
  type
}: {
  dealId: string;
  drivers: DealDriver[];
  label: string;
  scenarios: DealDriverScenario[];
  type: string;
}) => {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <Card>
        <CollapsibleTrigger className="flex w-full items-center justify-between">
          <h3 className="text-base font-semibold">{label}</h3>
          <ChevronDownIcon
            className={cn(
              'text-muted-foreground size-5 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-4 space-y-4">
            <DriversCard
              dealId={dealId}
              drivers={drivers}
              driverType={type}
            />
            <ScenariosCard
              dealId={dealId}
              driverType={type}
              scenarios={scenarios}
            />
          </div>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

// ── Main Tab Component ────────────────────────────────────────────

const KeyFinancialDriversTab = ({ dealId }: { dealId: string }) => {
  const { data: driversData, loading: driversLoading } = useQuery<
    GetAllDriversResponse,
    { dealOpportunityId: string }
  >(GET_ALL_DRIVERS, {
    variables: { dealOpportunityId: dealId }
  });

  const { data: scenariosData, loading: scenariosLoading } = useQuery<
    GetAllScenariosResponse,
    { dealOpportunityId: string }
  >(GET_ALL_SCENARIOS, {
    variables: { dealOpportunityId: dealId }
  });

  if (driversLoading || scenariosLoading) {
    return (
      <div className="grid h-64 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  const allDrivers = driversData?.allDrivers ?? [];
  const allScenarios = scenariosData?.allScenarios ?? [];

  return (
    <div className="space-y-2.5 pt-5">
      <div className="flex h-9 items-center">
        <h2 className="text-lg font-semibold">
          Key Financial Drivers & Model Sensitivities
        </h2>
      </div>
      <div className="space-y-5">
        {DRIVER_TYPES.map(({ label, value }) => (
          <DriverTypeSection
            dealId={dealId}
            drivers={allDrivers.filter(d => d.driverType === value)}
            key={value}
            label={label}
            scenarios={allScenarios.filter(s => s.driverType === value)}
            type={value}
          />
        ))}
      </div>
    </div>
  );
};

export default KeyFinancialDriversTab;
